package io.github.erkko68.filament

import io.github.erkko68.filament.capi.*
import io.github.erkko68.filament.interop.*

/**
 * Material defines the visual appearance of a surface.
 *
 * Materials are created from a binary blob generated by the material compiler (matc).
 * A Material is a template from which MaterialInstance objects can be created.
 *
 * **Creating instances:**
 * Use createInstance() to spawn an instance from this material. Instances inherit the material's
 * properties but can override parameters (color, texture, etc.) for per-object customization.
 * Always destroy instances with Engine.destroy() before destroying the material.
 *
 * **Material compilation:**
 * Use compile() to asynchronously compile specific material variants. This is useful for
 * precompiling variants you know will be used, reducing first-time rendering stalls.
 * After issuing multiple compile() calls, call Engine.flush() so the backend can start
 * compilation work.
 *
 * **Material properties:**
 * Material properties are read-only and determined at compile time from the matc-generated blob.
 * These include shading model, blending mode, transparency mode, and other rendering settings.
 * To override parameters per-instance, use MaterialInstance.
 */
class Material @InternalFilamentApi constructor(internal var nativeHandle: NativePointer) {
    /** The native object, for interop with code calling the Fila* C API directly. Read-only: this wrapper owns it. */
    @InternalFilamentApi
    val nativeObject: NativePointer get() = nativeHandle

    /**
     * Shading model determines how light interacts with the material surface.
     *
     * - UNLIT: No lighting applied; emissive possible. Useful for UI, billboards, glow effects
     * - LIT: Standard physically-based lighting (default)
     * - SUBSURFACE: Subsurface scattering for thin materials (skin, leaves, paper)
     * - CLOTH: Cloth-specific lighting model for fabric appearance
     * - SPECULAR_GLOSSINESS: Legacy shading model (use LIT for new materials)
     */
    enum class Shading { UNLIT, LIT, SUBSURFACE, CLOTH, SPECULAR_GLOSSINESS }
    /**
     * Attribute interpolation type in the fragment shader.
     *
     * - SMOOTH: Smooth interpolation (default); values smoothly transition across primitives
     * - FLAT: Flat interpolation; values are constant per primitive (no interpolation)
     */
    enum class Interpolation { SMOOTH, FLAT }
    /**
     * Blending mode determines how a material's color is combined with the background.
     *
     * - OPAQUE: Material is opaque; fully solid (default)
     * - TRANSPARENT: Transparent with alpha pre-multiplication; affects diffuse lighting only
     * - ADD: Additive blending (e.g., hologram, glow)
     * - MASKED: Alpha-tested (either fully opaque or fully transparent per pixel)
     * - FADE: Transparent with alpha pre-multiplication; affects specular lighting
     * - MULTIPLY: Darkens what's behind it
     * - SCREEN: Brightens what's behind it
     * - CUSTOM: Blend functions set by the material
     */
    enum class BlendingMode { OPAQUE, TRANSPARENT, ADD, MASKED, FADE, MULTIPLY, SCREEN, CUSTOM }
    /**
     * How transparent objects are rendered.
     *
     * - DEFAULT: Transparent object drawn honoring the raster state
     * - TWO_PASSES_ONE_SIDE: Object drawn in depth buffer, then color buffer with culling honored but depth test ignored
     * - TWO_PASSES_TWO_SIDES: Object drawn twice in color buffer (back faces, then front faces); culling ignored
     */
    enum class TransparencyMode { DEFAULT, TWO_PASSES_ONE_SIDE, TWO_PASSES_TWO_SIDES }
    /**
     * Vertex domain specifies which coordinate space vertices are defined in.
     *
     * - OBJECT: Vertices in object space (default)
     * - WORLD: Vertices in world space
     * - VIEW: Vertices in view/camera space
     * - DEVICE: Vertices in normalized device coordinates ([-1, 1])
     */
    enum class VertexDomain { OBJECT, WORLD, VIEW, DEVICE }
    /**
     * Culling mode determines which faces are rendered.
     *
     * - NONE: Both front and back faces rendered (disables culling)
     * - FRONT: Cull front faces; only back faces rendered
     * - BACK: Cull back faces; only front faces rendered (default)
     * - FRONT_AND_BACK: Cull all faces (usually used for testing or special effects)
     */
    enum class CullingMode { NONE, FRONT, BACK, FRONT_AND_BACK }
    /**
     * Priority queue for asynchronous material compilation.
     *
     * - CRITICAL: Highest priority; compiles immediately
     * - HIGH: High priority; compiles before LOW queue
     * - LOW: Low priority; compiles last
     *
     * @see compile
     */
    enum class CompilerPriorityQueue { CRITICAL, HIGH, LOW }
    /**
     * UBO batching mode controls whether material instances share uniform buffers.
     *
     * - DEFAULT: Use engine settings; if UBO batching is enabled and material domain is SURFACE, enable it
     * - DISABLED: Disable UBO batching for this material (each instance gets its own uniform buffer)
     */
    enum class UboBatchingMode { DEFAULT, DISABLED }

    /**
     * Uniform parameter types.
     *
     * - BOOL/BOOL2/BOOL3/BOOL4: Boolean scalar or vector
     * - FLOAT/FLOAT2/FLOAT3/FLOAT4: Floating-point scalar or vector
     * - INT/INT2/INT3/INT4: Signed integer scalar or vector
     * - UINT/UINT2/UINT3/UINT4: Unsigned integer scalar or vector
     * - MAT3/MAT4: 3x3 or 4x4 floating-point matrix
     * - STRUCT: A structure
     */
    enum class ParameterType {
        BOOL, BOOL2, BOOL3, BOOL4,
        FLOAT, FLOAT2, FLOAT3, FLOAT4,
        INT, INT2, INT3, INT4,
        UINT, UINT2, UINT3, UINT4,
        MAT3, MAT4, STRUCT
    }
    /** Precision of a numeric parameter. */
    enum class Precision { LOW, MEDIUM, HIGH, DEFAULT }
    /** Texture type of a sampler parameter. */
    enum class SamplerType { SAMPLER_2D, SAMPLER_2D_ARRAY, SAMPLER_CUBEMAP, SAMPLER_EXTERNAL, SAMPLER_3D, SAMPLER_CUBEMAP_ARRAY }
    /** Type of a subpass input parameter. */
    enum class SubpassType { SUBPASS_INPUT }

    /**
     * Holds information about a material parameter.
     *
     * Exactly one of [type], [samplerType] and [subpassType] is set, as [isSampler] and [isSubpass] say.
     */
    class ParameterInfo internal constructor(
        /** Name of the parameter. */
        val name: String,
        /** Whether the parameter is a sampler (texture). */
        val isSampler: Boolean,
        /** Whether the parameter is a subpass type. */
        val isSubpass: Boolean,
        /** Type of the parameter if it is neither a sampler nor a subpass. */
        val type: ParameterType?,
        /** Type of the parameter if it is a sampler. */
        val samplerType: SamplerType?,
        /** Type of the parameter if it is a subpass. */
        val subpassType: SubpassType?,
        /** Size of the parameter when it is an array. */
        val count: Int,
        /** Requested precision of the parameter. */
        val precision: Precision,
    )

    /**
     * Builder for creating Material objects from compiled material packages.
     *
     * The Builder takes a material package (binary blob from the matc compiler) and
     * configures rendering quality and batching options before building the final Material.
     */
    class Builder() {
        private val nativeBuilder = FilaMaterialBuilder_create()
        // Set in payload(): a non-empty blob that isn't a compiled .filamat. build() rejects it before
        // calling Filament's parser, which would otherwise panic uncatchably (see isValidFilamatPayload).
        private var payloadInvalid = false
        // Filament's Builder::package keeps the pointer (no copy) until build(), so the payload lives until then.
        private var payloadScope: InteropScope? = null
        /**
         * Shadow sampling quality affects how soft shadows are rendered.
         *
         * - HARD: 2x2 PCF (Percentage Closer Filtering); faster but harder shadows
         * - LOW: 3x3 Gaussian filter; slower but softer, smoother shadows
         */
        enum class ShadowSamplingQuality { HARD, LOW }

        /**
         * Specifies the material data (compiled binary blob); C++'s `package`, a Kotlin keyword.
         *
         * The material data is a binary blob produced by libfilamat or by matc (the material compiler).
         *
         * @param data The material data; kept until build() is called
         * @return This Builder, for chaining calls
         */
        fun payload(data: ByteArray): Builder = apply {
            if (data.isNotEmpty()) {
                payloadInvalid = !isValidFilamatPayload(data)
                releasePayload()
                payloadScope = InteropScope().also { FilaMaterialBuilder_package(nativeBuilder, it.toInterop(data, data.size), data.size) }
            } else {
                payloadInvalid = false
            }
        }
        /**
         * Specializes a constant parameter specified in the material definition with a concrete value for this
         * material. Once build() is called, this constant cannot be changed. Will panic if the name doesn't
         * correspond to a constant specified in the material definition, or if the type differs.
         *
         * @param name The name of the constant parameter specified in the material definition
         * @param value The value to use for the constant parameter
         * @return This Builder, for chaining calls.
         */
        fun constant(name: String, value: Int): Builder = apply { name.useCString { FilaMaterialBuilder_constant_int32_t(nativeBuilder, it, value) } }
        /** @see constant */
        fun constant(name: String, value: Float): Builder = apply { name.useCString { FilaMaterialBuilder_constant_float(nativeBuilder, it, value) } }
        /** @see constant */
        fun constant(name: String, value: Boolean): Builder = apply { name.useCString { FilaMaterialBuilder_constant_bool(nativeBuilder, it, value) } }
        /**
         * Sets the quality of the indirect light computations.
         *
         * This is only taken into account if the material is lit and in the surface domain.
         * This setting affects the IndirectLight computation if one is specified on the Scene
         * and Spherical Harmonics are used for the irradiance.
         *
         * @param shBandCount Number of spherical harmonic bands. Must be 1, 2 or 3 (default).
         * @return This Builder, for chaining calls.
         *
         * @see IndirectLight
         */
        fun sphericalHarmonicsBandCount(shBandCount: Int): Builder = apply {
            FilaMaterialBuilder_sphericalHarmonicsBandCount(nativeBuilder, shBandCount)
        }
        /**
         * Set the quality of shadow sampling.
         *
         * This is only taken into account if the material is lit and in the surface domain.
         *
         * @param quality Shadow sampling quality
         * @return This Builder, for chaining calls.
         */
        fun shadowSamplingQuality(quality: ShadowSamplingQuality): Builder = apply {
            FilaMaterialBuilder_shadowSamplingQuality(nativeBuilder, quality.ordinal)
        }
        /**
         * Set the batching mode of the instances created from this material.
         *
         * @param uboBatchingMode Batching mode to use
         * @return This Builder, for chaining calls.
         */
        fun uboBatching(uboBatchingMode: UboBatchingMode): Builder = apply {
            FilaMaterialBuilder_uboBatching(nativeBuilder, uboBatchingMode.ordinal)
        }
        private fun releasePayload() {
            payloadScope?.release()
            payloadScope = null
        }

        /**
         * Creates the Material and returns it.
         *
         * @param engine Engine to associate this Material with
         * @return The newly created Material, or null if the [payload] is not a valid compiled `.filamat`. The
         *   payload is checked before Filament's parser can panic, so callers can recover rather than crash.
         */
        fun build(engine: Engine): Material? {
            val handle = if (payloadInvalid) NullPointer else FilaMaterialBuilder_build(nativeBuilder, engine.nativeHandle)
            FilaMaterialBuilder_destroy(nativeBuilder)
            releasePayload()
            return if (handle == NullPointer) null else Material(handle)
        }
    }

    /**
     * Asynchronously compile a subset of this Material's variants.
     *
     * After issuing several compile() calls, it is recommended to call Engine.flush() so the
     * backend can start compilation work as soon as possible. The provided callback is guaranteed
     * to be called on the main thread after all specified variants are compiled. This can take
     * hundreds of milliseconds.
     *
     * If all variants are already compiled, the callback is scheduled as soon as possible, but
     * this might take a few dozen milliseconds depending on backend queue depth.
     *
     * If the same variant is scheduled for compilation multiple times, the first scheduling takes
     * precedence; later scheduling is ignored.
     *
     * **Note:** If you intend to override specialization constants on a per-MaterialInstance basis
     * via MaterialInstance, the programs compiled here may not be reusable. Call
     * MaterialInstance.compile() instead in those cases.
     *
     * @param priority Which priority queue to use (CRITICAL, HIGH, or LOW).
     * @param variants Variants to compile (a mask of [UserVariantFilterBit] values).
     * @param callback Optional callback invoked on the main thread with this material when compilation completes.
     */
    fun compile(priority: CompilerPriorityQueue, variants: Int = UserVariantFilterBit.ALL, callback: ((Material) -> Unit)? = null) {
        val userData = if (callback != null) Callbacks.register(once = true) { _ -> callback(this) } else NullPointer
        FilaMaterial_compile_UserVariantFilterMask_CallbackHandler_Invocable(
            nativeHandle, priority.ordinal, variants, NullPointer, if (callback != null) Callbacks.argUser else NullPointer, userData,
        )
    }

    /**
     * Creates a new instance of this material. Material instances should be freed using
     * Engine.destroy(MaterialInstance).
     *
     * @param name Name of the new instance for debugging; null inherits the material's name.
     * @return A new MaterialInstance with default parameters.
     */
    fun createInstance(name: String? = null): MaterialInstance = MaterialInstance(name.useCString { FilaMaterial_createInstance(nativeHandle, it) })
    /**
     * Get this material's default instance.
     *
     * The default instance is used for setDefaultParameter() calls on the Material itself.
     * Changes to the default instance affect all MaterialInstances that don't override those
     * parameters. The default instance is owned and managed by this Material.
     *
     * @return The default MaterialInstance.
     */
    val defaultInstance: MaterialInstance get() = MaterialInstance(FilaMaterial_getDefaultInstance(nativeHandle))

    /** The name of this material, from the matc-compiled blob. */
    val name: String get() = stringFromInterop(FilaMaterial_getName(nativeHandle)) ?: ""
    /** The shading model. */
    val shading: Shading get() = Shading.entries[FilaMaterial_getShading(nativeHandle)]
    /** The vertex attribute interpolation mode. */
    val interpolation: Interpolation get() = Interpolation.entries[FilaMaterial_getInterpolation(nativeHandle)]
    /** The blending mode. */
    val blendingMode: BlendingMode get() = BlendingMode.entries[FilaMaterial_getBlendingMode(nativeHandle)]
    /** The vertex coordinate space domain. */
    val vertexDomain: VertexDomain get() = VertexDomain.entries[FilaMaterial_getVertexDomain(nativeHandle)]
    /** The variants this material supports: a mask of [UserVariantFilterBit] values. */
    val supportedVariants: Int get() = FilaMaterial_getSupportedVariants(nativeHandle)
    /** The domain the material's shaders apply to. */
    val materialDomain: MaterialDomain get() = MaterialDomain.entries[FilaMaterial_getMaterialDomain(nativeHandle)]
    /** The face culling mode. */
    val cullingMode: CullingMode get() = CullingMode.entries[FilaMaterial_getCullingMode(nativeHandle)]
    /** The transparency rendering mode (only relevant for TRANSPARENT/FADE blending). */
    val transparencyMode: TransparencyMode get() = TransparencyMode.entries[FilaMaterial_getTransparencyMode(nativeHandle)]

    /** Whether color writes are enabled. */
    val isColorWriteEnabled: Boolean get() = FilaMaterial_isColorWriteEnabled(nativeHandle)
    /** Whether depth writes are enabled. */
    val isDepthWriteEnabled: Boolean get() = FilaMaterial_isDepthWriteEnabled(nativeHandle)
    /** Whether depth testing is enabled. */
    val isDepthCullingEnabled: Boolean get() = FilaMaterial_isDepthCullingEnabled(nativeHandle)
    /** Whether this material renders both front and back faces (double-sided). */
    val isDoubleSided: Boolean get() = FilaMaterial_isDoubleSided(nativeHandle)
    /**
     * Whether alpha-to-coverage is enabled.
     *
     * Alpha-to-coverage converts alpha values to a coverage mask for MSAA, improving
     * transparency rendering quality with MSAA.
     */
    val isAlphaToCoverageEnabled: Boolean get() = FilaMaterial_isAlphaToCoverageEnabled(nativeHandle)

    /**
     * The alpha threshold for MASKED blending mode, in [0, 1].
     *
     * Pixels with alpha >= threshold are rendered as fully opaque; below threshold are fully transparent.
     */
    val maskThreshold: Float get() = FilaMaterial_getMaskThreshold(nativeHandle)
    /** Whether this material uses the shadowing factor as a color multiplier (unlit materials only). */
    val hasShadowMultiplier: Boolean get() = FilaMaterial_hasShadowMultiplier(nativeHandle)
    /** Whether this material has specular anti-aliasing enabled. */
    val hasSpecularAntiAliasing: Boolean get() = FilaMaterial_hasSpecularAntiAliasing(nativeHandle)
    /** The screen-space variance of the specular anti-aliasing filter kernel, in [0, 1]. */
    val specularAntiAliasingVariance: Float get() = FilaMaterial_getSpecularAntiAliasingVariance(nativeHandle)
    /** The clamping threshold of specular anti-aliasing, in [0, 1]. */
    val specularAntiAliasingThreshold: Float get() = FilaMaterial_getSpecularAntiAliasingThreshold(nativeHandle)

    /**
     * The vertex attributes required by this material.
     *
     * Use this to determine which attributes your vertex buffers must provide. If a required
     * attribute is missing, rendering will fail or produce incorrect results.
     */
    val requiredAttributes: Set<VertexBuffer.VertexAttribute> get() {
        val bitset = FilaMaterial_getRequiredAttributes(nativeHandle)
        return VertexBuffer.VertexAttribute.entries.filterTo(mutableSetOf()) { (bitset and (1 shl it.value)) != 0 }
    }

    /** The refraction mode. */
    val refractionMode: RefractionMode get() = RefractionMode.entries[FilaMaterial_getRefractionMode(nativeHandle)]
    /** The refraction surface type. */
    val refractionType: RefractionType get() = RefractionType.entries[FilaMaterial_getRefractionType(nativeHandle)]
    /** The reflection mode. */
    val reflectionMode: ReflectionMode get() = ReflectionMode.entries[FilaMaterial_getReflectionMode(nativeHandle)]
    /** The minimum required feature level for this material. */
    val featureLevel: Engine.FeatureLevel get() = Engine.FeatureLevel.entries[FilaMaterial_getFeatureLevel(nativeHandle)]

    /** The number of parameters declared by this material. */
    val parameterCount: Int get() = FilaMaterial_getParameterCount(nativeHandle)

    /** Information about all parameters declared by this material. */
    val parameters: List<ParameterInfo> get() {
        val handles = List(parameterCount) { FilaMaterialParameterInfo_create() }
        try {
            val n = interopScope { FilaMaterial_getParameters(nativeHandle, toInterop(handles), handles.size) }
            return handles.take(n).map { p ->
                val isSampler = FilaMaterialParameterInfo_getIsSampler(p)
                val isSubpass = FilaMaterialParameterInfo_getIsSubpass(p)
                ParameterInfo(
                    stringFromInterop(FilaMaterialParameterInfo_getName(p)) ?: "",
                    isSampler,
                    isSubpass,
                    if (isSampler || isSubpass) null else ParameterType.entries[FilaMaterialParameterInfo_getType(p)],
                    if (isSampler) SamplerType.entries[FilaMaterialParameterInfo_getSamplerType(p)] else null,
                    if (isSubpass) SubpassType.entries[FilaMaterialParameterInfo_getSubpassType(p)] else null,
                    FilaMaterialParameterInfo_getCount(p),
                    Precision.entries[FilaMaterialParameterInfo_getPrecision(p)],
                )
            }
        } finally {
            handles.forEach { FilaMaterialParameterInfo_destroy(it) }
        }
    }

    /**
     * Indicates whether a parameter of the given name exists on this material.
     *
     * @param name Parameter name.
     * @return true if the parameter exists, false otherwise.
     */
    fun hasParameter(name: String): Boolean = name.useCString { FilaMaterial_hasParameter_char(nativeHandle, it) }
    /**
     * Indicates whether an existing parameter is a sampler or not.
     *
     * @param name Parameter name.
     * @return true if the parameter exists and is a sampler, false otherwise.
     */
    fun isSampler(name: String): Boolean = name.useCString { FilaMaterial_isSampler(nativeHandle, it) }
    /** The material's source, if the material was built with it (matc `-g`); empty otherwise. */
    val source: String get() = stringFromInterop(FilaMaterial_getSource(nativeHandle)) ?: ""
    /**
     * Get the transform parameter name associated with a sampler.
     *
     * Some samplers can have associated texture coordinate transforms (scale, rotation, etc).
     *
     * @param samplerName Name of a sampler parameter.
     * @return Transform parameter name if it exists, or null if not.
     */
    fun getParameterTransformName(samplerName: String): String? = samplerName.useCString { stringFromInterop(FilaMaterial_getParameterTransformName(nativeHandle, it)) }

    /** Sets a parameter on this material's default instance. @see MaterialInstance.setParameter */
    fun setDefaultParameter(name: String, x: Boolean) = defaultInstance.setParameter(name, x)
    /** @see setDefaultParameter */
    fun setDefaultParameter(name: String, x: Float) = defaultInstance.setParameter(name, x)
    /** @see setDefaultParameter */
    fun setDefaultParameter(name: String, x: Int) = defaultInstance.setParameter(name, x)
    /** @see setDefaultParameter */
    fun setDefaultParameter(name: String, x: Boolean, y: Boolean) = defaultInstance.setParameter(name, x, y)
    /** @see setDefaultParameter */
    fun setDefaultParameter(name: String, x: Float, y: Float) = defaultInstance.setParameter(name, x, y)
    /** @see setDefaultParameter */
    fun setDefaultParameter(name: String, x: Int, y: Int) = defaultInstance.setParameter(name, x, y)
    /** @see setDefaultParameter */
    fun setDefaultParameter(name: String, x: Boolean, y: Boolean, z: Boolean) = defaultInstance.setParameter(name, x, y, z)
    /** @see setDefaultParameter */
    fun setDefaultParameter(name: String, x: Float, y: Float, z: Float) = defaultInstance.setParameter(name, x, y, z)
    /** @see setDefaultParameter */
    fun setDefaultParameter(name: String, x: Int, y: Int, z: Int) = defaultInstance.setParameter(name, x, y, z)
    /** @see setDefaultParameter */
    fun setDefaultParameter(name: String, x: Boolean, y: Boolean, z: Boolean, w: Boolean) = defaultInstance.setParameter(name, x, y, z, w)
    /** @see setDefaultParameter */
    fun setDefaultParameter(name: String, x: Float, y: Float, z: Float, w: Float) = defaultInstance.setParameter(name, x, y, z, w)
    /** @see setDefaultParameter */
    fun setDefaultParameter(name: String, x: Int, y: Int, z: Int, w: Int) = defaultInstance.setParameter(name, x, y, z, w)
    /** @see setDefaultParameter */
    fun setDefaultParameter(name: String, texture: Texture?, sampler: TextureSampler) = defaultInstance.setParameter(name, texture, sampler)
    /** @see setDefaultParameter */
    fun setDefaultParameter(name: String, type: RgbType, r: Float, g: Float, b: Float) = defaultInstance.setParameter(name, type, r, g, b)
    /** @see setDefaultParameter */
    fun setDefaultParameter(name: String, type: RgbaType, r: Float, g: Float, b: Float, a: Float) = defaultInstance.setParameter(name, type, r, g, b, a)
}

// Every compiled .filamat begins with the MaterialVersion chunk, whose 8-byte type token is
// "MAT_VERSION" stored little-endian — i.e. the bytes "SREV_TAM" on disk. Builder.build() checks
// this before handing the payload to Filament's parser, which panics on a non-.filamat blob; that
// panic unwinds across the prebuilt's -fno-exceptions frames and terminates the process. Bailing
// here turns the common bad-asset case (a PNG, a truncated download) into a null Material.
private val FILAMAT_MAGIC = "SREV_TAM".encodeToByteArray()

/** True if [data] starts with the compiled-`.filamat` magic. Cheap structural sniff, not a full parse. */
internal fun isValidFilamatPayload(data: ByteArray): Boolean =
    data.size >= FILAMAT_MAGIC.size && FILAMAT_MAGIC.indices.all { data[it] == FILAMAT_MAGIC[it] }
